import React, { useEffect, useState } from "react";
import { Box, Paper, Button, Grid, Snackbar, Alert } from "@mui/material";
import { useMasterData } from "../../../hooks/useMasterData";
import ReportFilters from "./ReportFilters";
import ReportTable from "./ReportTable";
import { reportApi, ReportRow } from "./reportApi";

const ReportsPage: React.FC = () => {
  const { masters, fetchMasters, fetchAcademicSessions, fetchSections } = useMasterData();
  const [reportType, setReportType] = useState("CLASS_WISE");
  const [filters, setFilters] = useState<Record<string, any>>({});
  const [sections, setSections] = useState<{ id: string; name: string }[]>([]);
  const [rows, setRows] = useState<ReportRow[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" | "info" }>({
    open: false,
    message: "",
    severity: "info",
  });

  useEffect(() => {
    fetchMasters();
    fetchAcademicSessions();
  }, []);

  useEffect(() => {
    if (filters.classId) {
      fetchSections(filters.classId).then(setSections);
    } else {
      setSections([]);
      setFilters((p) => ({ ...p, sectionId: "" }));
    }
  }, [filters.classId]);

  const queryParams = () => ({
    reportType,
    sessionId: filters.sessionId || undefined,
    classId: filters.classId || undefined,
    sectionId: filters.sectionId || undefined,
    categoryId: filters.categoryId || undefined,
    houseId: filters.houseId || undefined,
    religionId: filters.religionId || undefined,
    studentTypeId: filters.studentTypeId || undefined,
    status: filters.status || undefined,
    rte: reportType === "RTE" ? "true" : undefined,
    bpl: reportType === "BPL" ? "true" : undefined,
    fromDate: filters.fromDate || undefined,
    toDate: filters.toDate || undefined,
    page,
    limit: pageSize,
  });

  const generate = async () => {
    try {
      const res = await reportApi.fetch(queryParams());
      setRows(res.items);
      setTotal(res.total);
    } catch (e: any) {
      setSnackbar({ open: true, message: e.response?.data?.message || "Failed to fetch report", severity: "error" });
    }
  };

  const exportExcel = async () => {
    try {
      const blob = await reportApi.exportExcel(queryParams());
      const nameMap: Record<string, string> = {
        ADMISSION_REGISTER: "Admission Register",
        CLASS_WISE: "Class Wise Students",
        CATEGORY: "Category Report",
        HOUSE_WISE: "House Wise Students",
        RTE: "RTE Students",
        BPL: "BPL Students",
        STATUS: "Active - Inactive Students",
        RELIGION: "Religion Wise Report",
        STUDENT_TYPE: "Student Type Report",
      };
      const fileName = nameMap[reportType] || "Student Report";
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${fileName}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      setSnackbar({ open: true, message: e.response?.data?.message || "Failed to export", severity: "error" });
    }
  };

  const exportPdf = async () => {
    const nameMap: Record<string, string> = {
      ADMISSION_REGISTER: "Admission Register",
      CLASS_WISE: "Class Wise Students",
      CATEGORY: "Category Report",
      HOUSE_WISE: "House Wise Students",
      RTE: "RTE Students",
      BPL: "BPL Students",
      STATUS: "Active - Inactive Students",
      RELIGION: "Religion Wise Report",
      STUDENT_TYPE: "Student Type Report",
    };
    const fileName = nameMap[reportType] || "Student Report";
    // Build printable HTML
    const headers = [
      "Admission No",
      "Scholar No",
      "Student Name",
      "Father Name",
      "Class",
      "Section",
      "Mobile",
      "Status",
      "Category",
      "House",
      "Religion",
      "Student Type",
    ];
    const htmlRows = rows
      .map(
        (r) =>
          `<tr>
            <td>${r.admissionNumber || ""}</td>
            <td>${r.scholarNumber || ""}</td>
            <td>${r.studentName || ""}</td>
            <td>${r.fatherName || ""}</td>
            <td>${r.class || ""}</td>
            <td>${r.section || ""}</td>
            <td>${r.mobile || ""}</td>
            <td>${r.status || ""}</td>
            <td>${r.category || ""}</td>
            <td>${r.house || ""}</td>
            <td>${r.religion || ""}</td>
            <td>${r.studentType || ""}</td>
          </tr>`
      )
      .join("");
    const html = `
      <html>
        <head>
          <title>${fileName}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 16px; }
            h2 { margin: 0 0 12px; }
            table { border-collapse: collapse; width: 100%; }
            th, td { border: 1px solid #444; padding: 6px; font-size: 12px; }
            th { background: #f0f0f0; }
          </style>
        </head>
        <body>
          <h2>${fileName}</h2>
          <table>
            <thead>
              <tr>${headers.map((h) => `<th>${h}</th>`).join("")}</tr>
            </thead>
            <tbody>
              ${htmlRows}
            </tbody>
          </table>
        </body>
      </html>
    `;
    // Use hidden iframe to avoid popup blockers
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    document.body.appendChild(iframe);
    const doc = iframe.contentDocument || iframe.ownerDocument;
    if (!doc) return;
    doc.open();
    doc.write(html);
    doc.close();
    // Ensure PDF file name uses the report type by setting document.title explicitly
    doc.title = fileName;
    iframe.onload = () => {
      // Redundantly set title on contentWindow for browsers using window title
      if (iframe.contentWindow?.document) {
        iframe.contentWindow.document.title = fileName;
      }
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
      setTimeout(() => {
        document.body.removeChild(iframe);
      }, 1000);
    };
  };

  const reset = () => {
    setFilters({});
    setRows([]);
    setTotal(0);
    setPage(1);
    setPageSize(20);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ fontSize: 22, fontWeight: 600, mb: 2 }}>Student Reports</Box>
      <Paper sx={{ p: 2, mb: 2 }}>
        <ReportFilters
          masters={masters as any}
          sections={sections}
          filters={filters}
          onChange={setFilters}
          reportType={reportType}
          onReportTypeChange={setReportType}
        />
      </Paper>
      <Grid container spacing={1} sx={{ mb: 2 }}>
        <Grid item>
          <Button variant="contained" onClick={generate}>
            Generate Report
          </Button>
        </Grid>
        <Grid item>
          <Button variant="outlined" onClick={exportExcel}>
            Export Excel
          </Button>
        </Grid>
        <Grid item>
          <Button variant="outlined" onClick={exportPdf}>
            Print / Export PDF
          </Button>
        </Grid>
        <Grid item>
          <Button onClick={reset}>Reset</Button>
        </Grid>
      </Grid>
      <ReportTable rows={rows} total={total} page={page} pageSize={pageSize} onPageChange={setPage} onPageSizeChange={setPageSize} />
      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar((p) => ({ ...p, open: false }))}>
        <Alert severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReportsPage;
